import { Component, Inject, Input, OnInit, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { CategoryService } from 'lib/services/category/category.service';
import { RouterService } from 'lib/services/router.service';
import { WebpService } from 'lib/services/webp.service';
import { Location } from '@angular/common';
import { BootstrapMarketoService } from 'lib/services/bootstrap/bootstrap-marketo.service';

@Component({
    selector: 'cms-partial-category-top-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule],
})
export class Video extends CmsPartialComponentClass implements AfterViewInit {
    @Input() videoColumnsRow: any;
    @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;
    getWebpImg: (src: string) => string;
    private isIOS: boolean;
    private isSafari: boolean;

    constructor(
        @Inject('environment') environment: any,
        @Inject(PLATFORM_ID) private platformId: Object,
        public bootstrapMarketoService: BootstrapMarketoService,
        routerService: RouterService,
        webpService: WebpService,
        location: Location,
        categoryService: CategoryService
    ) {
        super({
            dependencies: {
                environment,
                bootstrapMarketoService,
                routerService,
                location,
                categoryService,
                webpService,
            },
        });
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
        if (isPlatformBrowser(this.platformId)) {
            this.detectDevice();
        }
    }

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.initializeVideoPlayback();
    }

    private detectDevice(): void {
        const userAgent = navigator.userAgent;
        this.isIOS = /iPad|iPhone|iPod/.test(userAgent);
        this.isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    }

    private initializeVideoPlayback(): void {
        if (this.videoElement?.nativeElement) {
            const video = this.videoElement.nativeElement;

            // Set initial properties for iOS Safari compatibility
            video.muted = true;
            video.playsInline = true;

            // Handle different events for reliable playback
            video.addEventListener('loadedmetadata', () => {
                this.attemptVideoPlay(video);
            });

            video.addEventListener('canplay', () => {
                this.attemptVideoPlay(video);
            });

            // With SSR + incremental hydration the video can load before these listeners exist
            if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
                this.attemptVideoPlay(video);
            }

            // For iOS Safari, also try on user interaction
            if (this.isIOS && this.isSafari) {
                this.setupUserInteractionPlay(video);
            }
        }
    }

    private async attemptVideoPlay(video: HTMLVideoElement): Promise<void> {
        try {
            // Ensure video is muted for autoplay policies
            video.muted = true;

            const playPromise = video.play();

            if (playPromise !== undefined) {
                await playPromise;
                console.log('Video started playing successfully');
            }
        } catch (error) {
            console.log('Autoplay failed, will require user interaction:', error);

            // If autoplay fails, especially on iOS Safari, set up click handler
            if (this.isIOS && this.isSafari) {
                this.setupUserInteractionPlay(video);
            }
        }
    }

    private setupUserInteractionPlay(video: HTMLVideoElement): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const playOnInteraction = () => {
            video.muted = true;
            video
                .play()
                .then(() => {
                    console.log('Video started playing after user interaction');
                    // Remove the event listener after successful play
                    document.removeEventListener('touchstart', playOnInteraction);
                    document.removeEventListener('click', playOnInteraction);
                })
                .catch((err) => {
                    console.error('Failed to play video even after user interaction:', err);
                });
        };

        // Listen for any user interaction on the document
        document.addEventListener('touchstart', playOnInteraction, { once: true });
        document.addEventListener('click', playOnInteraction, { once: true });
    }

    public onVideoError(event: any): void {
        console.error('Video error occurred:', event);
    }

    public onVideoLoaded(): void {
        if (this.videoElement?.nativeElement) {
            this.attemptVideoPlay(this.videoElement.nativeElement);
        }
    }
}
